import CustomerDataService from '@root/modules/customer/services/CustomerData.service';

export type ListenedId = { id: string; value: string };
export type ListenedDuration = { id: string; key: string; value: string };

type ListenedIdsResponse = {
  items: ListenedId[];
  pager: {
    count: number;
    limit: number;
    offset: number;
  };
};

export type GetListenedIds = (value?: string, token?: string, limit?: number, offset?: number) => Promise<ListenedIdsResponse | null>;
export type CreateListenedId = (value: string, token?: string) => Promise<boolean>;
export type GetListenedDuration = (articleIds: string[], token?: string) => Promise<ListenedDuration[] | null>;
export type CreateListenedDuration = (articleId: string, value: string, token?: string) => Promise<boolean>;
export type UpdateListenedDuration = (articleId: string, key: string, value: string, token?: string) => Promise<boolean>;
export type HandleListenedDurationUpdate = (articleId: string, value: string, token?: string) => Promise<void>;

export const getListenedIds: GetListenedIds = async (value, token, limit = 10, offset = 0) => {
  if (!token) {
    return null;
  }

  const input = {
    key: ['podcast-listened-id'],
    value,
    limit,
    offset,
  };

  const customerDataService = new CustomerDataService();
  customerDataService.setCustomerToken(token);

  const response = await customerDataService.get(input);

  return response?.data || null;
};

export const createListenedId: CreateListenedId = async (value, token) => {
  if (!token) {
    return false;
  }

  // Check if record is not already created
  const check = await getListenedIds(value, token, 1);
  if (check?.items.length) {
    return false;
  }

  const customerDataService = new CustomerDataService();
  customerDataService.setCustomerToken(token);

  const response = await customerDataService.create({ key: 'podcast-listened-id', value });

  if (response?.data?.status) {
    return true;
  }

  return false;
};

export const getListenedDuration: GetListenedDuration = async (articleIds, token) => {
  if (!token) {
    return null;
  }

  const key = articleIds.map((articleId) => `podcast-listened-duration--${articleId}`);

  const input = {
    key,
    pager: {
      orderBy: 'CREATED_AT',
    },
  };

  const customerDataService = new CustomerDataService();
  customerDataService.setCustomerToken(token);

  const response = await customerDataService.get(input);

  if (response?.data?.items?.length) {
    return response.data.items;
  }

  return null;
};

export const createListenedDuration: CreateListenedDuration = async (articleId, value, token) => {
  if (!token) {
    return false;
  }

  const input = {
    key: `podcast-listened-duration--${articleId}`,
    value,
  };

  const customerDataService = new CustomerDataService();
  customerDataService.setCustomerToken(token);

  const response = await customerDataService.create(input);

  if (response?.data?.status) {
    return true;
  }

  return false;
};

export const updateListenedDuration: UpdateListenedDuration = async (id, articleId, value, token) => {
  if (!token) {
    return false;
  }

  const input = {
    id,
    key: `podcast-listened-duration--${articleId}`,
    value,
  };

  const customerDataService = new CustomerDataService();
  customerDataService.setCustomerToken(token);

  const response = await customerDataService.update(input);

  if (response?.data?.status) {
    return true;
  }

  return false;
};

export const handleListenedDurationUpdate: HandleListenedDurationUpdate = async (articleId, value, token) => {
  if (!token) {
    return;
  }

  // Check if record is not already created
  const check = await getListenedDuration([articleId], token);

  if (check?.length) {
    const id = check[0].id;

    await updateListenedDuration(id, articleId, value, token);
  } else {
    await createListenedDuration(articleId, value, token);
  }
};
